import { useEffect } from "react";
import { Link } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import CTA from "../components/CTA";

// SEO: JSON-LD structured data — Organization + Service catalog + FAQ.
// Injected on mount so Googlebot picks it up when crawling the SPA (post-render).
const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "@id": "https://shobhawithshubhra.com/#business",
      name: "Shobha with Shubhra LLC",
      description:
        "Wisconsin-based luxury wedding and event planning studio. Full-service design, planning and delivery for weddings, graduations, baby showers, corporate events, birthdays and milestone celebrations.",
      url: "https://shobhawithshubhra.com/",
      email: "contact@shobhawithshubhra.com",
      telephone: "+1-262-527-4592",
      areaServed: { "@type": "State", name: "Wisconsin" },
      address: { "@type": "PostalAddress", addressRegion: "WI", addressCountry: "US" },
      priceRange: "$$$",
    },
    {
      "@type": "Service",
      serviceType: "Wedding Planning",
      provider: { "@id": "https://shobhawithshubhra.com/#business" },
      areaServed: "Wisconsin",
    },
    {
      "@type": "Service",
      serviceType: "Graduation Party Planning",
      provider: { "@id": "https://shobhawithshubhra.com/#business" },
      areaServed: "Wisconsin",
    },
    {
      "@type": "Service",
      serviceType: "Baby Shower Planning",
      provider: { "@id": "https://shobhawithshubhra.com/#business" },
      areaServed: "Wisconsin",
    },
    {
      "@type": "Service",
      serviceType: "Corporate Event Planning",
      provider: { "@id": "https://shobhawithshubhra.com/#business" },
      areaServed: "Wisconsin",
    },
    {
      "@type": "Service",
      serviceType: "Birthday Party Planning",
      provider: { "@id": "https://shobhawithshubhra.com/#business" },
      areaServed: "Wisconsin",
    },
    {
      "@type": "Service",
      serviceType: "Anniversary Celebration Planning",
      provider: { "@id": "https://shobhawithshubhra.com/#business" },
      areaServed: "Wisconsin",
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Which areas of Wisconsin do you serve?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "We plan and design events throughout Wisconsin, including Milwaukee, Madison, Green Bay, Sheboygan and the surrounding lake country.",
          },
        },
        {
          "@type": "Question",
          name: "How far in advance should we book?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "We recommend six to twelve months for full weddings and two to three months for smaller events like graduations or baby showers. Popular Saturday dates fill quickly, so earlier is better.",
          },
        },
        {
          "@type": "Question",
          name: "Do you offer full-service planning or day-of coordination?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Both. Full-service planning covers design, vendors and management from concept to breakdown. Day-of coordination is available for couples who have planned themselves but want a professional running the day.",
          },
        },
        {
          "@type": "Question",
          name: "What kinds of weddings do you plan?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "We plan Indian, South Asian, Western and multicultural weddings — from intimate ceremonies to multi-day celebrations with sangeet, mehendi, pheras and reception.",
          },
        },
        {
          "@type": "Question",
          name: "How does pricing work?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Every event is unique. During your complimentary consultation Shubhra will share transparent pricing based on your guest count, venue and design scope.",
          },
        },
      ],
    },
  ],
};

const SERVICES = [
  {
    id: "wedding-planning",
    title: "Wedding Planning",
    kicker: "SIGNATURE EVENTS",
    intro:
      "Indian, South Asian and Western weddings across Wisconsin — designed with intention, delivered with quiet precision.",
    body:
      "From the first paper suite to the vidaai, we shape every hour of your wedding weekend. Mandap and floral design, vendor curation, guest logistics, sangeet and mehendi styling, ceremony choreography and reception production — all led by a small team who show up on the day so you can be a guest at your own celebration.",
    image: "https://i.imgur.com/rJS8Fvv.jpeg",
  },
  {
    id: "graduation-parties",
    title: "Graduation Parties",
    kicker: "MILESTONE MOMENTS",
    intro:
      "Backyards, banquet halls, private clubs — memorable graduation celebrations for high-school and college graduates.",
    body:
      "A graduation only happens once. We help families plan a party that captures the personality of the graduate — signature colors, memory walls, curated menu, cake styling, photo activations and, when the day comes, on-site coordination so parents can relax and celebrate alongside their guests.",
    image: "https://i.imgur.com/hZztQD7.jpeg",
  },
  {
    id: "baby-shower",
    title: "Baby Showers",
    kicker: "WELCOMING NEW LIFE",
    intro:
      "Elegant baby showers with heirloom detail — florals, dessert design, backdrops and thoughtful guest experience.",
    body:
      "Whether you are planning a traditional godh bharai or a modern Sunday brunch shower, we build a warm, personal atmosphere. Backdrops and dessert tables designed to your palette, curated florals, favors that feel considered, and light coordination so the mom-to-be is genuinely present in every moment.",
    image: "https://i.imgur.com/YPzrFh3.jpeg",
  },
  {
    id: "corporate-events",
    title: "Corporate & Cultural Events",
    kicker: "BRAND-BUILDING GATHERINGS",
    intro:
      "Product launches, milestone dinners, cultural galas and executive retreats — planned with polish.",
    body:
      "We understand the pressure of corporate calendars. Venue sourcing, agenda design, guest communications, brand-aligned decor and vendor management — all delivered on schedule and on brand. Wisconsin businesses across financial services, healthcare and the arts trust us with events that need to feel effortless.",
    image: "https://i.imgur.com/i04lY5N.jpeg",
  },
  {
    id: "birthday-parties",
    title: "Birthdays & Milestone Parties",
    kicker: "CELEBRATIONS THAT LAST",
    intro:
      "Sweet 16s, decade birthdays and quinceañeras — themed, styled and coordinated end-to-end.",
    body:
      "We start with the person and build outward. A Bollywood-themed Sweet 16, a Great Gatsby fortieth, a garden-party tenth — every element chosen so guests remember the atmosphere as much as the cake. Custom backdrops, entertainment coordination, staged photo moments and calm on-site management.",
    image: "https://i.imgur.com/ClZeiEs.jpeg",
  },
  {
    id: "anniversary-events",
    title: "Anniversary Celebrations",
    kicker: "HONORING LOVE OVER TIME",
    intro:
      "Silver, ruby, pearl and golden anniversaries — celebrations designed for the family that has grown with you.",
    body:
      "Anniversaries carry meaning that new milestones cannot. We build multi-generational celebrations with tribute videos, curated menus, live music, floral installations and heirloom-worthy tablescapes. Intimate dinners of twenty or grand vow renewals of two hundred — always calibrated to the couple at the center.",
    image: "https://i.imgur.com/fMGUSuy.jpeg",
  },
];

const FAQS = [
  {
    q: "Which areas of Wisconsin do you serve?",
    a: "We plan and design events throughout Wisconsin — including Milwaukee, Madison, Green Bay, Sheboygan and the surrounding lake country.",
  },
  {
    q: "How far in advance should we book?",
    a: "Six to twelve months for full weddings, and two to three months for smaller events like graduations or baby showers. Popular Saturday dates fill quickly.",
  },
  {
    q: "Do you offer full-service planning or day-of coordination?",
    a: "Both. Full-service planning covers design, vendors and management from concept to breakdown. Day-of coordination is available for couples who have planned themselves but want a professional running the day.",
  },
  {
    q: "What kinds of weddings do you plan?",
    a: "We plan Indian, South Asian, Western and multicultural weddings — from intimate ceremonies to multi-day celebrations with sangeet, mehendi, pheras and reception.",
  },
  {
    q: "How does pricing work?",
    a: "Every event is unique. During your complimentary consultation Shubhra will share transparent pricing based on your guest count, venue and design scope.",
  },
];

export default function Services() {
  useReveal();

  // Inject JSON-LD, document title and meta description for SEO
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = "Wedding & Event Planning Services in Wisconsin | Shobha with Shubhra";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Full-service wedding, graduation, baby shower, corporate, birthday and anniversary event planning across Wisconsin. Book a complimentary consultation with Shubhra."
      );
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "sws-services-ld";
    script.text = JSON.stringify(JSON_LD);
    document.head.appendChild(script);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      const s = document.getElementById("sws-services-ld");
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="pt-32">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16">
        <div className="kicker reveal">Services</div>
        <h1 className="font-display text-4xl md:text-7xl leading-[1.02] mt-6 max-w-4xl reveal">
          Wisconsin's <span className="italic">full-service</span> studio for weddings, graduations and celebrations that matter.
        </h1>
        <p className="mt-8 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed reveal">
          Design, planning and delivery of exceptional events — from Milwaukee to Madison, Green Bay to
          Sheboygan. Whether you're marrying the love of your life, welcoming a child, celebrating a graduation
          or honoring a milestone, we shape days that guests remember for the atmosphere as much as the moment.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 reveal">
          <CTA to="/contact" label="Book a consultation" />
          <Link
            to="/portfolio"
            className="text-[11px] tracking-[0.3em] uppercase border-b border-[hsl(var(--ink))] pb-1 hover:text-[hsl(var(--accent))]"
          >
            See recent work
          </Link>
        </div>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-24">
          {SERVICES.map((s, i) => (
            <article key={s.id} id={s.id} className={`reveal ${i % 2 ? "md:mt-24" : ""}`}>
              <div className="img-zoom">
                <img src={s.image} alt={s.title} className="w-full h-[440px] object-cover" />
              </div>
              <div className="kicker mt-6">{s.kicker}</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">{s.title}</h2>
              <p className="mt-4 text-[hsl(var(--ink-soft))] leading-relaxed max-w-md">{s.intro}</p>
              <p className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed max-w-md">{s.body}</p>
              <div className="mt-6"><CTA to="/contact" label="Enquire" /></div>
            </article>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="bg-[hsl(var(--bg-alt))] py-24">
        <div className="mx-auto max-w-[1200px] px-6 grid md:grid-cols-3 gap-10 text-center">
          <div className="reveal">
            <div className="font-display text-5xl md:text-6xl italic">Local</div>
            <p className="mt-4 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
              A Wisconsin studio with real relationships across our best venues, florists, caterers and
              entertainment partners.
            </p>
          </div>
          <div className="reveal">
            <div className="font-display text-5xl md:text-6xl italic">Personal</div>
            <p className="mt-4 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
              Every event is led by Shubhra — no handoffs to junior planners, no cookie-cutter templates.
            </p>
          </div>
          <div className="reveal">
            <div className="font-display text-5xl md:text-6xl italic">Composed</div>
            <p className="mt-4 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
              Meticulous timelines and calm on-site management mean you and your family stay present in
              every moment.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[1000px] px-6 md:px-10 py-24">
        <div className="kicker reveal">Frequently Asked</div>
        <h2 className="font-display text-3xl md:text-5xl mt-6 leading-tight reveal">
          Answers to the <span className="italic">questions</span> we hear most.
        </h2>
        <dl className="mt-12 space-y-8">
          {FAQS.map((f) => (
            <div key={f.q} className="border-t border-[hsl(var(--line))] pt-6 reveal">
              <dt className="font-display text-xl md:text-2xl">{f.q}</dt>
              <dd className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Closing CTA */}
      <section className="bg-[hsl(var(--ink))] text-[hsl(var(--bg))] py-24">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <div className="kicker opacity-70">Next step</div>
          <h2 className="font-display text-3xl md:text-5xl mt-6 leading-tight">
            Tell us about your event.
          </h2>
          <p className="mt-6 max-w-xl mx-auto opacity-80 leading-relaxed">
            The complimentary consultation is where we listen — to your story, your family, and the kind
            of day you want to remember. From there we build a plan that fits your budget and your vision.
          </p>
          <div className="mt-10 flex justify-center">
            <Link
              to="/contact"
              className="inline-block bg-[hsl(var(--bg))] text-[hsl(var(--ink))] px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--bg))] transition-colors duration-500"
            >
              Book a consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
